
import React from 'react';
import { KeyContact } from '@/types/lead';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from '@/components/ui/badge';
import { Trash2, Edit2, Mail, Phone, Briefcase, Linkedin, UserX } from 'lucide-react';

interface ContactCardProps {
  contact: KeyContact;
  onEdit: (contact: KeyContact) => void;
  onDelete: (contact: KeyContact) => void;
}

const sourceLabels: Record<string, { label: string; className: string }> = {
  manual: { label: 'Manual', className: 'bg-muted text-muted-foreground' },
  web_search: { label: 'Web Search', className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800' },
  deep_research: { label: 'Deep Research', className: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800' },
};

const ContactCard: React.FC<ContactCardProps> = ({ contact, onEdit, onDelete }) => {
  const sourceInfo = sourceLabels[contact.source || 'manual'] || sourceLabels.manual;
  
  // Extract LinkedIn URL from either the dedicated field or notes
  const linkedinUrl = contact.linkedinUrl || 
    contact.notes?.match(/LinkedIn Profile: (https?:\/\/[^\s\n]+)/)?.[1];

  return (
    <Card className={`group relative hover:shadow-lg transition-all duration-300 border-border hover:border-border/80 animate-fade-in ${contact.status === 'left' ? 'opacity-60' : ''}`}>
      <div className="absolute -top-2 right-3 flex gap-1.5 z-10">
        {contact.status === 'left' && (
          <Badge 
            variant="outline" 
            className="bg-destructive/10 border-destructive/30 text-destructive shadow-sm font-semibold backdrop-blur-sm text-xs"
          >
            <UserX className="h-3 w-3 mr-1" />
            Excluded
          </Badge>
        )}
        {contact.isPrimary && (
          <Badge 
            variant="outline" 
            className="bg-amber-50/80 border-amber-300 text-amber-800 shadow-sm font-semibold backdrop-blur-sm text-xs dark:bg-amber-950/80 dark:text-amber-300 dark:border-amber-700"
          >
            Primary
          </Badge>
        )}
        {contact.source && contact.source !== 'manual' && (
          <Badge 
            variant="outline" 
            className={`shadow-sm font-medium backdrop-blur-sm text-xs ${sourceInfo.className}`}
          >
            {sourceInfo.label}
          </Badge>
        )}
      </div>
      
      <CardHeader className="pb-3 space-y-1.5 px-4 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <CardTitle className="text-lg">{contact.name}</CardTitle>
            {contact.position && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-md inline-flex w-fit">
                <Briefcase className="h-3 w-3" />
                <span>{contact.position}</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 px-4 pb-4">
        <div className="space-y-2">
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-2 text-sm text-foreground font-medium hover:text-primary/80 hover:underline underline-offset-4 transition-colors group/link"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/5 text-primary group-hover/link:bg-primary/10 transition-colors">
                <Mail className="h-3.5 w-3.5" />
              </div>
              <span className="text-xs">{contact.email}</span>
            </a>
          )}
          
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="flex items-center gap-2 text-sm text-foreground font-medium hover:text-primary/80 hover:underline underline-offset-4 transition-colors group/link"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/5 text-primary group-hover/link:bg-primary/10 transition-colors">
                <Phone className="h-3.5 w-3.5" />
              </div>
              <span className="text-xs">{contact.phone}</span>
            </a>
          )}

          {linkedinUrl && (
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-foreground font-medium hover:text-primary/80 hover:underline underline-offset-4 transition-colors group/link"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-md bg-[#0A66C2]/5 text-[#0A66C2] group-hover/link:bg-[#0A66C2]/10 transition-colors">
                <Linkedin className="h-3.5 w-3.5" />
              </div>
              <span className="text-xs">View LinkedIn</span>
            </a>
          )}
        </div>

        {contact.notes && !contact.notes.startsWith('Auto-added') && (
          <div className="pt-3 mt-3 border-t border-border/50">
            <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed bg-muted/20 p-2 rounded-md">
              {contact.notes}
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-1.5 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(contact)}
            className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-border"
          >
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(contact)}
            className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive transition-colors border border-transparent hover:border-border"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactCard;
